import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import type { SnapCarouselAPI } from '@elfsight/lib-snap-carousel';
import { SnapCarousel } from '@elfsight/lib-snap-carousel';
import { Tooltip } from '@elfsight/widget-modal';
import { Typography } from '@elfsight/widget-ui';
import { createReadableClass, getInvertedColor } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { format, isSameDay, isToday } from 'date-fns';
import { capitalize } from 'es-toolkit';
import { useRect } from 'react-use-rect';
import { DatetimePickerButton } from '../components/datetime-picker-button';
import { NavigationButton } from '../components/navigation-button';
import {
  BODY2_TEXT_STYLE,
  BUTTON_TEXT_STYLE,
  Title2
} from '../components/typography';
import { useEventContext } from '../event-provider';
import { useServices } from '../service/services-provider';
import { useDatetimeFormat } from '../utils/use-datetime-format';
import { useWidgetDimensions } from '../widget-dimensions-provider';
import { CurrentDay } from './current-day';
import type { DayWithTimeslots } from './datetime-provider';
import { useDatetime } from './datetime-provider';

export function DaysCarousel() {
  const carouselRef = useRef<SnapCarouselAPI | null>(null);
  const { textColor } = useSettings();
  const { isDayAvailable, days, selectedDay, setSelectedDay } = useDatetime();
  const { services } = useServices();
  const { isMobile } = useWidgetDimensions();
  const { t } = useLingui();
  const { formatWeekday } = useDatetimeFormat();
  const { subscribe } = useEventContext();

  const [carouselIndex, setCarouselIndex] = useState(0);
  const [width, setWidth] = useState<number | undefined>();
  const [measureRef] = useRect(({ width }) => setWidth(width), {
    resize: true
  });

  const displayedDays = isMobile ? 5 : 7;
  const itemWidth = width
    ? (width - 8 * (displayedDays - 1)) / displayedDays
    : undefined;

  const handleIntervalShift = (dir: 1 | -1) => {
    const next = carouselIndex + dir * displayedDays;
    if (carouselRef.current) {
      carouselRef.current.slideToColumn(next);
    }
  };

  const handleSlideChange = (index: number) => {
    setCarouselIndex(index);
  };

  useEffect(() => {
    const slideToInitial = (day?: DayWithTimeslots) => {
      if (!day) {
        return;
      }

      const next = days.findIndex(({ date }) => isSameDay(day.date, date));

      if (carouselRef.current && next) {
        carouselRef.current.slideToColumn(next, true);
      }
    };

    if (selectedDay) {
      slideToInitial(selectedDay);
    }

    const unsubscribe = subscribe('firstAvailableDaySet', slideToInitial);

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <Container ref={measureRef}>
      {selectedDay && services.length > 0 && (
        <Header>
          <CurrentDay />
          <Navigation>
            <NavigationButton
              ariaLabel="Back"
              disabled={carouselIndex === 0}
              direction="backward"
              onClick={() => handleIntervalShift(-1)}
            />
            <NavigationButton
              ariaLabel="Forward"
              direction="forward"
              disabled={!days[carouselIndex + displayedDays]}
              onClick={() => handleIntervalShift(1)}
            />
          </Navigation>
        </Header>
      )}

      <SnapCarousel
        columns={7}
        columnsGap={8}
        ref={carouselRef}
        withArrows={false}
        onSlideChange={handleSlideChange}
        durationTransition={600}
      >
        {days.map((day) => {
          const { date } = day;
          const key = format(date, 'm-d');
          const selected = !!selectedDay && isSameDay(date, selectedDay.date);
          const available = isDayAvailable(day) && services.length > 0;

          const handleClick = () => {
            if (available) {
              setSelectedDay(day);
            }
          };

          const renderedDay = (
            <Day
              key={key}
              selected={selected}
              disabled={!available}
              tabIndex={available ? 0 : -1}
              role={available ? 'button' : undefined}
              style={{ width: itemWidth }}
              onClick={handleClick}
            >
              <Weekday
                textStyle={selected ? BUTTON_TEXT_STYLE : BODY2_TEXT_STYLE}
                lineHeight="20px"
                _isMobile={isMobile}
              >
                {isToday(date)
                  ? t`today`.replace(/\S+/g, capitalize)
                  : formatWeekday(date, 'short')}
              </Weekday>
              <Date _isMobile={isMobile}>{format(date, 'd')}</Date>
            </Day>
          );

          if (!available) {
            return (
              <Tooltip
                key={`${key}-tooltip`}
                fontColor={getInvertedColor(textColor)}
                background={textColor}
                title={t`No available slots on this day`}
              >
                {renderedDay}
              </Tooltip>
            );
          }

          return renderedDay;
        })}
      </SnapCarousel>
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'days-carousel',
    element: 'container'
  })
})`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

const Day = styled(DatetimePickerButton).attrs({
  className: createReadableClass({
    component: 'days-carousel',
    element: 'day'
  })
})`
  padding: 12px 12px 8px 12px;
  gap: 4px;
  height: auto !important;
  overflow: hidden;
`;

const Header = styled.div.attrs({
  className: createReadableClass({
    component: 'days-carousel',
    element: 'header'
  })
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-height: 22px;
`;

const Navigation = styled.div.attrs({
  className: createReadableClass({
    component: 'days-carousel',
    element: 'navigation'
  })
})``;

const Weekday = styled(Typography).attrs({
  className: createReadableClass({
    component: 'days-carousel',
    element: 'weekday'
  })
})<{ _isMobile: boolean }>`
  ${({ _isMobile }) =>
    _isMobile &&
    css`
      font-size: 14px;
    `}
`;

const Date = styled(Title2).attrs({
  className: createReadableClass({
    component: 'days-carousel',
    element: 'date'
  })
})<{ _isMobile: boolean }>`
  ${({ _isMobile }) =>
    _isMobile &&
    css`
      font-size: 15px;
    `}
`;
